import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Map, Briefcase, Users, Building, Building2, Car, BarChart3, Shield, LogOut, Wifi, WifiOff, HelpCircle, Settings, Clock, CreditCard } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';

const navItems = [
  { to: '/',          label: 'Dashboard',  icon: LayoutDashboard },
  { to: '/map',       label: 'Harita',     icon: Map },
  { to: '/jobs',      label: 'İşler',      icon: Briefcase },
  { to: '/customers', label: 'Müşteriler', icon: Building2, adminOnly: true },
  { to: '/vehicles',  label: 'Araçlar',    icon: Car, adminOnly: true },
  { to: '/reports',   label: 'Raporlar',   icon: BarChart3, adminOnly: true },
  { to: '/users',     label: 'Personel',   icon: Users, adminOnly: true },
  { to: '/billing',   label: 'Fatura & Plan', icon: CreditCard, adminOnly: true },
  { to: '/settings',  label: 'Firma Ayarları', icon: Settings, adminOnly: true },
  { to: '/settings/branches', label: 'Şubeler', icon: Building, adminOnly: true },
  { to: '/settings/working-hours', label: 'Çalışma Saatleri', icon: Clock, adminOnly: true },
  { to: '/support',   label: 'Destek',     icon: HelpCircle },
];

export function Sidebar() {
  const { user, logout } = useAuth();
  const { connected } = useSocket();

  return (
    <aside className="w-60 bg-slate-900 flex flex-col h-screen sticky top-0 shrink-0">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-slate-700 shrink-0">
        <div className="flex items-center gap-2.5">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" rx="8" fill="#0A0F1E"/>
            <circle cx="16" cy="14" r="10" stroke="#2563EB" strokeOpacity="0.15" strokeWidth="0.75"/>
            <circle cx="16" cy="14" r="6.5" stroke="#2563EB" strokeOpacity="0.25" strokeWidth="0.75"/>
            <circle cx="16" cy="14" r="3" stroke="#2563EB" strokeOpacity="0.35" strokeWidth="0.75"/>
            <line x1="6" y1="14" x2="26" y2="14" stroke="#2563EB" strokeOpacity="0.12" strokeWidth="0.5"/>
            <line x1="16" y1="4" x2="16" y2="24" stroke="#2563EB" strokeOpacity="0.12" strokeWidth="0.5"/>
            <circle cx="16" cy="14" r="4" fill="#2563EB"/>
            <circle cx="16" cy="14" r="1.5" fill="white"/>
            <polygon points="16,21 13.5,16 18.5,16" fill="#2563EB"/>
          </svg>
          <span className="text-[17px] font-extrabold tracking-tight">
            <span className="text-white">field</span><span className="text-blue-500">pin</span>
          </span>
        </div>
        <div className={`mt-2 flex items-center gap-1.5 text-xs ${connected ? 'text-green-400' : 'text-slate-500'}`}>
          {connected ? <Wifi size={11} /> : <WifiOff size={11} />}
          {connected ? 'Canlı bağlantı' : 'Bağlantı yok'}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto min-h-0">
        {navItems.map(({ to, label, icon: Icon, adminOnly }) => {
          if (adminOnly && !['admin', 'supervisor', 'superadmin'].includes(user?.role)) return null;
          return (
            <NavLink
              key={to}
              to={to}
              end={to === '/' || to === '/settings'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                ${isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'}`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          );
        })}
        {user?.role === 'superadmin' && (
          <NavLink to="/ops-center-f9x2"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mt-2 border-t border-slate-700 pt-3
              ${isActive ? 'bg-amber-600 text-white' : 'text-amber-400 hover:text-white hover:bg-slate-800'}`}>
            <Shield size={18} />
            Super Admin
          </NavLink>
        )}
      </nav>

      {/* Mobil Uygulama */}
      <div className="px-3 py-3 border-t border-slate-700 shrink-0">
        <p className="text-xs text-slate-500 px-1 pb-2 font-medium">Mobil Uygulama</p>
        <a
          href="https://apps.apple.com/tr/app/fieldpin/id6761395268"
          target="_blank"
          rel="noopener noreferrer"
          className="block hover:opacity-80 transition-opacity px-1"
        >
          <img
            src="/badges/app-store-badge.svg"
            alt="App Store'dan İndirin"
            className="h-10 w-auto"
          />
        </a>
      </div>

      {/* User */}
      <div className="px-4 py-4 border-t border-slate-700 shrink-0">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-slate-400 text-xs truncate">{user?.company_name || user?.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg text-sm transition-colors"
        >
          <LogOut size={16} />
          Çıkış Yap
        </button>
      </div>
    </aside>
  );
}